import React, { useEffect, useRef, useState } from 'react';
import { Editor } from '@monaco-editor/react';
import { Script } from '@/features/_data/useScripts';
import { Flex } from '@/ui/Flex/Flex';
import { playerSdkTypes } from './playerSdkTypes/player.sdk';
import { StyledCodeEditorWrapper, StyledWrapper } from './styles';
import ContentPayloadSidebar from './ContentPayloadSidebar';

type CodeEditorProps = {
  setCode: React.Dispatch<React.SetStateAction<string>>;
  script?: Script;
  isEditingCode: boolean;
};

function CodeEditor({ setCode, script, isEditingCode }: CodeEditorProps) {
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isContentPayloadSidebarOpen, setIsContentPayloadSidebarOpen] =
    useState(false);

  // Layout on editing mode change
  useEffect(() => {
    if (editorRef.current) {
      // layout() needs to be called to resize the editor
      // this has to be done with an empty object, otherwise it won't work, god knows why
      // https://stackoverflow.com/questions/59963794/monaco-editor-force-resize-editor/64485730#64485730
      editorRef.current.layout({});
    }
  }, [isEditingCode, isContentPayloadSidebarOpen]);

  return (
    <StyledWrapper data-testid="code-editor">
      <ContentPayloadSidebar
        contentPayload={script?.contentPayload}
        isContentPayloadSidebarOpen={isContentPayloadSidebarOpen}
        setIsContentPayloadSidebarOpen={setIsContentPayloadSidebarOpen}
      />
      <Flex grow padding={16}>
        <StyledCodeEditorWrapper ref={containerRef}>
          <Editor
            beforeMount={(monaco) => {
              monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(
                {
                  noSemanticValidation: false,
                  noSyntaxValidation: false,
                },
              );
              monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
                {
                  target: monaco.languages.typescript.ScriptTarget.ES2016,
                  allowNonTsExtensions: true,
                },
              );

              const libUri = `ts:filename/player-sdk.d.ts`;
              monaco.languages.typescript.javascriptDefaults.addExtraLib(
                playerSdkTypes,
                libUri,
              );

              if (!monaco.editor.getModel(monaco.Uri.parse(libUri))) {
                monaco.editor.createModel(
                  playerSdkTypes,
                  'typescript',
                  monaco.Uri.parse(libUri),
                );
              }
            }}
            defaultLanguage="typescript"
            onChange={(value, ev) => {
              if (!ev.isFlush) setCode(value ?? '');
            }}
            onMount={(editor) => {
              editorRef.current = editor;
            }}
            options={{
              minimap: { enabled: false },
              folding: false,
              lineDecorationsWidth: 8,
              lineNumbersMinChars: 5,
              fontSize: 14,
              detectIndentation: true,
              readOnly: !isEditingCode,
            }}
            theme="vs-dark"
            value={script?.script}
          />
        </StyledCodeEditorWrapper>
      </Flex>
    </StyledWrapper>
  );
}

export default CodeEditor;
